import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PiMagnifyingGlass, PiSparkleBold, PiArrowLeftBold } from "react-icons/pi";
import { toast } from "react-toastify";
import axios from "../../axios";
import apiError from "../../error";
import CustomChatTable from "../chat/custom-chat-table";
import "./ai-create-group.css";

const PAGE_LIMIT = 10;

// Debounce helper kept local so the component has no external coupling.
function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const AiCreateGroup = ({ onClose, onCreated }) => {
  const [groupName, setGroupName] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [targets, setTargets] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [creating, setCreating] = useState(false);
  const [prompt, setPrompt] = useState("");

  // ── AI prompt → matching-users preview state ──
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPreview, setAiPreview] = useState(null); // { previewId, reportList, totalCount, rule, reportName }

  // Progress for large (>= 1000 user) groups that the backend builds in a
  // background job. null while no background creation is running.
  const [progress, setProgress] = useState(null); // { percent, processed, total }
  const pollTimerRef = useRef(null);
  const pollActiveRef = useRef(false);

  // Stop polling if the panel unmounts mid-creation.
  useEffect(() => () => {
    pollActiveRef.current = false;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
  }, []);

  const debouncedSearch = useDebouncedValue(search, 500);

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/targets/fetchAll/0", {
        params: { page, limit: PAGE_LIMIT, search: debouncedSearch },
      });
      setTargets(res.data?.response?.targets ?? []);
      setTotalCount(res.data?.response?.totalTargetsCount ?? 0);
    } catch (err) {
      apiError(err);
      setTargets([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => {
    if (aiPreview) return; // manual list paused while AI preview is shown
    fetchTargets();
  }, [fetchTargets, aiPreview]);

  // Reset to first page whenever the search term changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const isSelected = useCallback(
    (target) => selectedTargets.some((t) => t.id === target.id),
    [selectedTargets]
  );

  const toggleTarget = (target) => {
    setSelectedTargets((prev) =>
      prev.some((t) => t.id === target.id)
        ? prev.filter((t) => t.id !== target.id)
        : [...prev, target]
    );
  };

  const allOnPageSelected =
    targets.length > 0 && targets.every((target) => isSelected(target));

  const togglePage = () => {
    setSelectedTargets((prev) => {
      if (allOnPageSelected) {
        return prev.filter((t) => !targets.some((target) => target.id === t.id));
      }
      const merged = [...prev];
      targets.forEach((target) => {
        if (!merged.some((t) => t.id === target.id)) merged.push(target);
      });
      return merged;
    });
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_LIMIT));

  const rangeLabel = useMemo(() => {
    if (totalCount === 0) return "0";
    const start = (page - 1) * PAGE_LIMIT + 1;
    const end = Math.min(page * PAGE_LIMIT, totalCount);
    return `${start}-${end} of ${totalCount}`;
  }, [page, totalCount]);

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }
    if (selectedTargets.length === 0) {
      toast.error("Please select at least one user");
      return;
    }

    setCreating(true);
    try {
      const groupRes = await axios.post("/api/groups", { name: groupName.trim() });
      const groupId = groupRes.data?.groupId;
      await axios.post("/api/groups/assign-targets", {
        groupId,
        targets: selectedTargets,
      });
      toast.success("Group created successfully");
      if (onCreated) {
        onCreated({ id: groupId, name: groupName.trim(), users: selectedTargets.length });
      }
    } catch (err) {
      apiError(err);
    } finally {
      setCreating(false);
    }
  };

  // ── AI flow ──
  const handleAiPreview = async () => {
    const text = prompt.trim();
    if (!text || aiLoading) return;
    setAiLoading(true);
    try {
      const res = await axios.post("/api/groups/ai-preview", { prompt: text });
      const data = res.data || {};
      setAiPreview({
        previewId: data.previewId,
        reportList: Array.isArray(data.reportList) ? data.reportList : [],
        totalCount: typeof data.totalCount === "number" ? data.totalCount : 0,
        headers: Array.isArray(data.headers) ? data.headers : [],
        status: data.status || "draft",
        customReportId: data.customReportId || data.previewId,
        customReport: data.customReport || {},
      });
      const reportName = data.customReport?.name || "";
      if (reportName && !groupName.trim()) setGroupName(reportName);
    } catch (err) {
      apiError(err);
    } finally {
      setAiLoading(false);
    }
  };

  // Polls the background-creation status endpoint until the group is fully
  // built, driving the progress bar. The post-create side effect (onCreated)
  // only runs once the group reaches "completed".
  const trackBackgroundCreation = (data) =>
    new Promise((resolve) => {
      const groupId = data.groupId;
      pollActiveRef.current = true;
      setProgress({ percent: 0, processed: 0, total: data.totalCount || 0 });

      const poll = async () => {
        if (!pollActiveRef.current) return resolve();
        try {
          const statusRes = await axios.get(`/api/groups/${groupId}/ai-create-status`);
          const s = statusRes.data || {};
          if (!pollActiveRef.current) return resolve();

          setProgress({
            percent: typeof s.percent === "number" ? s.percent : 0,
            processed: s.processed || 0,
            total: s.total || data.totalCount || 0,
          });

          if (s.failed) {
            pollActiveRef.current = false;
            toast.error("Group creation failed. Please try again.");
            setProgress(null);
            setCreating(false);
            return resolve();
          }

          if (s.done) {
            pollActiveRef.current = false;
            setProgress((p) => ({ ...(p || {}), percent: 100 }));
            const userCount = s.total || s.processed || data.totalCount;
            toast.success(`Group created with ${userCount} user(s)`);
            if (onCreated) {
              onCreated({ id: groupId, name: s.name || data.name, users: userCount });
            }
            return resolve();
          }
        } catch (err) {
          // Transient polling errors are ignored; the next tick retries.
        }
        pollTimerRef.current = setTimeout(poll, 1500);
      };

      poll();
    });

  const handleAiCreate = async () => {
    if (!aiPreview) return;
    if (!groupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }
    if (!aiPreview.totalCount) {
      toast.error("No users match this condition");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post("/api/groups/ai-create", {
        name: groupName.trim(),
        previewId: aiPreview.previewId,
      });
      const data = res.data || {};

      // Large audience: the backend is building the group in a background job.
      // Show a progress bar and defer the side effect until it completes.
      if (data.processing) {
        await trackBackgroundCreation(data);
        return;
      }

      // Small audience: created synchronously.
      toast.success(`Group created with ${data.userCount} user(s)`);
      if (onCreated) {
        onCreated({ id: data.groupId, name: data.name, users: data.userCount });
      }
    } catch (err) {
      apiError(err);
      setProgress(null);
      setCreating(false);
    }
  };

  // ── AI preview view ──
  if (aiPreview) {
    return (
      <div className="acg-wrapper">
        <div className="acg-header">
          <div className="acg-header-text">
            <h5 className="acg-title">
              <PiSparkleBold className="acg-title-spark" /> AI Matched Users
            </h5>
            <p className="acg-subtitle">
              Review the users matching your condition, then create the group
            </p>
          </div>
          <button className="acg-close-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="acg-card">
          <button
            type="button"
            className="acg-back-link"
            onClick={() => setAiPreview(null)}
            disabled={creating}
          >
            <PiArrowLeftBold /> Edit condition
          </button>

          {/* AI-reports table: auto-renders the condition + matching data */}
          <CustomChatTable
            previewId={aiPreview.previewId}
            headers={aiPreview.headers}
            reportList={aiPreview.reportList}
            totalCount={aiPreview.totalCount}
            status={aiPreview.status}
            customReportId={aiPreview.customReportId}
            customReport={aiPreview.customReport}
            isGroupPreview={true}
          />

          {/* <div className="acg-matched-banner">
            The group will include <strong>all {aiPreview.totalCount}</strong> user(s)
            matching this condition.
          </div> */}
        </div>

        <div className="acg-card">
          <label className="acg-field-label">Group Name</label>
          <input
            className="acg-name-input"
            value={groupName}
            placeholder="Enter group name"
            onChange={(e) => setGroupName(e.target.value)}
          />
        </div>

        {progress ? (
          <div className="acg-progress">
            <div className="acg-progress-head">
              <PiSparkleBold className="acg-progress-spark" />
              <span className="acg-progress-label">Creating your group…</span>
              <span className="acg-progress-pct">{progress.percent}%</span>
            </div>
            <div className="acg-progress-track">
              <div
                className="acg-progress-fill"
                style={{ width: `${progress.percent}%` }}
              />
            </div>
            <div className="acg-progress-sub">
              {progress.total
                ? `${(progress.processed || 0).toLocaleString()} of ${(progress.total || 0).toLocaleString()} users added`
                : "Preparing users…"}
            </div>
          </div>
        ) : (
          <div className="acg-footer">
            <button className="acg-cancel-btn" onClick={onClose} disabled={creating}>
              Cancel
            </button>
            <button
              className="acg-create-btn"
              onClick={handleAiCreate}
              disabled={creating || !groupName.trim() || !aiPreview.totalCount}
            >
              {creating ? "Creating…" : `Create Group (${aiPreview.totalCount} users)`}
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Default: manual selection + AI prompt bar ──
  return (
    <div className="acg-wrapper">
      {/* ── Header ── */}
      <div className="acg-header">
        <div className="acg-header-text">
          <h5 className="acg-title">Create Group</h5>
          <p className="acg-subtitle">Select the users you want to add</p>
        </div>
        <button className="acg-close-btn" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>

      {/* ── Group Name ── */}
      <div className="acg-card">
        <label className="acg-field-label">Group Name</label>
        <input
          className="acg-name-input"
          value={groupName}
          placeholder="Enter group name"
          onChange={(e) => setGroupName(e.target.value)}
        />
      </div>

      {/* ── User Selection ── */}
      <div className="acg-card">
        <div className="acg-card-head">
          <span className="acg-field-label">Select Users</span>
          <span className="acg-selected-count">
            {selectedTargets.length} selected
          </span>
        </div>

        <div className="acg-search">
          <PiMagnifyingGlass />
          <input
            type="search"
            value={search}
            placeholder="Search users"
            aria-label="Search users"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="acg-table-wrap">
          <table className="acg-table">
            <thead>
              <tr>
                <th className="acg-checkbox-col">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={togglePage}
                    disabled={targets.length === 0}
                  />
                </th>
                <th>Name</th>
                <th>Designation</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={3} className="acg-empty-row">
                    Loading users…
                  </td>
                </tr>
              ) : targets.length === 0 ? (
                <tr>
                  <td colSpan={3} className="acg-empty-row">
                    {debouncedSearch ? "No matching users found." : "No users found."}
                  </td>
                </tr>
              ) : (
                targets.map((target) => (
                  <tr
                    key={target.IdentityKey || target.id}
                    className={isSelected(target) ? "acg-row-selected" : ""}
                    onClick={() => toggleTarget(target)}
                  >
                    <td className="acg-checkbox-col">
                      <input
                        type="checkbox"
                        checked={isSelected(target)}
                        onChange={() => toggleTarget(target)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td>
                      <div className="acg-user-cell">
                        <div className="acg-avatar">
                          {(target.firstname || "?")[0]}
                        </div>
                        <div className="acg-user-info">
                          <span className="acg-user-name">
                            {target.firstname} {target.lastname}
                          </span>
                          <span className="acg-user-email">{target.email}</span>
                        </div>
                      </div>
                    </td>
                    <td>{target.designation || target.position || "---"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="acg-pagination">
          <button
            type="button"
            className="acg-page-btn"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </button>
          <span className="acg-page-info">{rangeLabel}</span>
          <button
            type="button"
            className="acg-page-btn"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>

      {/* ── Create with Prompt → AI matching-users preview ── */}
      <div className="acg-prompt-section">
        <span className="acg-prompt-label">
          <PiSparkleBold /> Create group with prompt
        </span>
        <div className={`acg-prompt-bar ${aiLoading ? "is-loading" : ""}`}>
          <span className="acg-clip-icon">📎</span>
          <input
            className="acg-prompt-input"
            placeholder={
              aiLoading
                ? "Finding matching users…"
                : "e.g. all users in the Finance department with EVS above 50"
            }
            value={prompt}
            disabled={aiLoading}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAiPreview()}
          />
          <button
            type="button"
            className="acg-send-btn"
            title="Find matching users"
            disabled={aiLoading || !prompt.trim()}
            onClick={handleAiPreview}
          >
            {aiLoading ? (
              <span className="acg-send-spinner" aria-hidden />
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="acg-footer">
        <button className="acg-cancel-btn" onClick={onClose} disabled={creating}>
          Cancel
        </button>
        <button
          className="acg-create-btn"
          onClick={handleCreateGroup}
          disabled={creating || !groupName.trim() || selectedTargets.length === 0}
        >
          {creating ? "Creating…" : "Create Group"}
        </button>
      </div>
    </div>
  );
};

export default AiCreateGroup;
